import { type SheetInstance, sheetStore } from '@shared-core/navigation/sheet-store'
import { useSheetPortals } from '@web/hooks/ui/use-sheet-portals'
import { isRegisteredSheetKey, SHEET_REGISTRY } from '@web/sheets/registry'
import { SheetContainerDrawerContent } from '@web/sheets/sheet-container-drawer-content'
import { SheetDrawerPortal } from '@web/sheets/sheet-drawer-portal'
import { useSheetStyles } from '@web/sheets/styles'
import { useMemo } from 'react'
import { Drawer } from 'vaul'

type SheetContainerProps = {
  instance: SheetInstance
  layerIndex: number
  isTopLayer: boolean
}

// Один шит: vaul Drawer даёт выезд снизу, затемнённый оверлей, drag-to-dismiss
// и инерцию из коробки. `open` управляется флагом closing из стора, чтобы
// корректно проигрывалась анимация выезда перед удалением из стека.
export function SheetContainer({ instance, layerIndex, isTopLayer }: SheetContainerProps) {
  const { portalContainer, useBodyPortal } = useSheetPortals()
  const ctxValue = useMemo(() => ({ id: instance.id, params: instance.params }), [instance.id, instance.params])

  const sheetEntry = isRegisteredSheetKey(instance.key) ? SHEET_REGISTRY[instance.key] : null
  const open = !instance.closing
  const isFullscreen = false
  const styles = useSheetStyles({
    layerIndex,
    useBodyPortal,
    isFullscreen,
  })

  if (!sheetEntry) return null

  const { Component, options } = sheetEntry
  const snapPoints = options?.snapPoints
  const isDismissible = options?.dismissible ?? true
  // Граббер только если шторку можно свайпнуть вниз.
  const isHandleVisible = isDismissible && !isFullscreen

  return (
    <Drawer.Root
      open={open}
      dismissible={isTopLayer && isDismissible}
      snapPoints={snapPoints}
      activeSnapPoint={snapPoints ? snapPoints[0] : undefined}
      // Как ServiceBottomSheet: vaul по умолчанию раздувает шторку до visualViewport
      // при фокусе инпута (repositionInputs) — короткие формы (название карты) улетают
      // вверх с огромным зазором над клавиатурой. Подъём — через bottom + keyboard inset.
      repositionInputs={false}
      onOpenChange={(next) => {
        // Закрытие по свайпу / оверлею / Esc — через history.back (как и Назад),
        // чтобы запись истории снималась. hasOpen() — актуальное состояние, без stale closure.
        if (!next && isTopLayer && sheetStore.hasOpen()) window.history.back()
      }}
      onAnimationEnd={(isOpen) => {
        // Анимация выезда вниз завершилась — убираем из стека.
        if (!isOpen) sheetStore.remove(instance.id)
      }}
    >
      <SheetDrawerPortal
        portalContainer={portalContainer}
        isBodyPortal={useBodyPortal}
        overlayStyle={styles.overlay}
        centeredHostStyle={styles.centeredHost}
      >
        <SheetContainerDrawerContent
          styles={styles}
          ctxValue={ctxValue}
          Component={Component}
          isHandleVisible={isHandleVisible}
        />
      </SheetDrawerPortal>
    </Drawer.Root>
  )
}
